import { useSecureGraphqlMutation } from '@psyomics/ui-common';
import { useEffect, useMemo, useRef } from 'react';

import {
  InitialiseInviteSurveyDocument,
  InitialiseInviteSurveyInput,
  InitialiseInviteSurveyMutation,
} from '../generated/survey';

const inFlightInviteIds = new Set<string>();

type ResultType = NonNullable<InitialiseInviteSurveyMutation['initialiseInviteSurvey']>;
type ParsedResult = Omit<ResultType, 'survey'> & {
  survey: {
    definition: unknown;
  };
};
const useInviteSurvey = (invitePublicId: string) => {
  const currentInviteIdRef = useRef<string | undefined>(undefined);

  const [{ data, fetching, errorMessage }, executeMutation] = useSecureGraphqlMutation<
    InitialiseInviteSurveyMutation,
    { input: InitialiseInviteSurveyInput },
    'InviteNotFound'
  >(InitialiseInviteSurveyDocument, ['InviteNotFound' as const]);

  const hasExecutedMutationForInvite = currentInviteIdRef.current === invitePublicId;

  useEffect(() => {
    if (hasExecutedMutationForInvite || inFlightInviteIds.has(invitePublicId)) {
      return;
    }

    inFlightInviteIds.add(invitePublicId);

    void executeMutation({ input: { invitePublicId } }).finally(() => {
      inFlightInviteIds.delete(invitePublicId);
    });

    currentInviteIdRef.current = invitePublicId;
  }, [hasExecutedMutationForInvite, invitePublicId, executeMutation]);

  const result = data?.initialiseInviteSurvey;

  const parsedResult = useMemo<ParsedResult | undefined>(() => {
    if (!result?.survey.definition) {
      return undefined;
    }

    return {
      ...result,
      survey: {
        definition: JSON.parse(result.survey.definition) as unknown,
      },
    };
  }, [result]);

  return {
    result: parsedResult,
    fetching: fetching || !hasExecutedMutationForInvite,
    errorMessage,
  };
};

export type { ParsedResult };

export { useInviteSurvey };
